import { cn } from '@core/utils';
import Link from '@tiptap/extension-link';
import { NodeSelection } from '@tiptap/pm/state';

export interface CustomLinkOptions {
  href: string;
  theme:
    | 'text'
    | 'primary-fill-button'
    | 'sub-fill-button'
    | 'blue-fill-button'
    | 'image';
  isOutLink: boolean;
  /** 버튼 형식에만 영향을 줌 */
  size: 'H56' | 'H40';
}

// 테마 → CSS 클래스 매핑
export const BUTTON_THEME_CLASS_MAP = {
  'primary-fill-button': 'bg-Gray-black text-Gray-white',
  'sub-fill-button': 'bg-Gray-200 text-Gray-black',
  'blue-fill-button': 'bg-B-500 text-Gray-white',
} as const;

// 사이즈 → CSS 클래스 매핑
export const BUTTON_SIZE_CLASS_MAP = {
  H56: 'py-16 px-24 typo-body3',
  H40: 'py-9 px-16 typo-body4',
} as const;

// CSS 클래스 → 테마 역매핑 (Toolbar에서 기존 링크 감지용)
export const CLASS_TO_BUTTON_THEME = {
  'bg-Gray-black': 'primary-fill-button',
  'bg-Gray-200': 'sub-fill-button',
  'bg-B-500': 'blue-fill-button',
} as const;

// CSS 클래스 → 사이즈 역매핑
export const CLASS_TO_BUTTON_SIZE = {
  'py-9': 'H40',
  'py-16': 'H56',
} as const;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customLink: {
      setCustomLink: (options: CustomLinkOptions) => ReturnType;
    };
  }
}

export const CustomLink = Link.configure({
  autolink: false,
  openOnClick: false,
  protocols: ['http', 'https'],
  linkOnPaste: false,
}).extend({
  addAttributes() {
    return {
      ...this.parent?.(),
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setCustomLink:
        ({ href, theme, size, isOutLink }) =>
        ({ chain, state }) => {
          const { selection } = state;
          const { from, to } = selection;

          const updateImageAttributes = () => {
            return chain()
              .updateAttributes('image', {
                href,
                target: isOutLink ? '_blank' : '_self',
                rel: isOutLink ? 'noopener noreferrer' : undefined,
              })
              .run();
          };

          // 이미지 노드가 선택되었는지 확인
          const isImageSelected =
            selection instanceof NodeSelection &&
            selection.node.type.name === 'image';

          if (isImageSelected) {
            return updateImageAttributes();
          }

          switch (theme) {
            case 'image':
              return updateImageAttributes();

            case 'primary-fill-button':
            case 'sub-fill-button':
            case 'blue-fill-button':
              return chain()
                .extendMarkRange('link')
                .unsetMark('link')
                .setMark('link', {
                  href,
                  class: cn(
                    'inline-block cursor-pointer rounded-small font-700',
                    BUTTON_SIZE_CLASS_MAP[size],
                    BUTTON_THEME_CLASS_MAP[theme],
                  ),
                  rel: isOutLink ? 'noopener noreferrer' : undefined,
                  target: isOutLink ? '_blank' : '_self',
                })
                .setTextSelection({ from, to })
                .run();

            case 'text':
              return chain()
                .extendMarkRange('link')
                .unsetMark('link')
                .setMark('link', {
                  href,
                  class: 'cursor-pointer hover:underline text-B-500 font-500',
                  rel: isOutLink ? 'noopener noreferrer' : undefined,
                  target: isOutLink ? '_blank' : '_self',
                })
                .setTextSelection({ from, to })
                .run();
          }
        },
    };
  },

  addPasteRules() {
    return [];
  },
});
